// src/pages/CheckEmail.tsx
// Step 2 of 5 - Email OTP verification
// Styled to match app dashboard

import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { getSupabase } from "@/lib/supabase";

function maskEmail(e: string) {
  const [name, domain] = e.split("@");
  if (!domain) return e;
  const n = name.length <= 2 ? name[0] || "*" : `${name[0]}***${name[name.length - 1]}`;
  return `${n}@${domain}`;
}

function StepIndicator({ current, total }: { current: number; total: number }) {
  return (
    <div className="flex items-center justify-center gap-2">
      {Array.from({ length: total }, (_, i) => (
        <div
          key={i}
          className={`h-1.5 rounded-full transition-all duration-500 ${
            i < current 
              ? "w-8 bg-[#a78bfa]" 
              : i === current 
                ? "w-8 bg-[#a78bfa]/40" 
                : "w-3 bg-[#f1f5f9]"
          }`}
        />
      ))}
    </div>
  );
}

export default function CheckEmail() {
  const email = localStorage.getItem("signup_email") || "";
  const [code, setCode] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [resent, setResent] = useState(false);
  
  const nav = useNavigate();
  const supabase = getSupabase();
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  useEffect(() => {
    // Auto-focus first input
    setTimeout(() => inputRefs.current[0]?.focus(), 100);
  }, []);

  const verify = async () => {
    setErr(null);
    if (!email) return setErr("Missing email. Please start over.");
    if (code.length !== 6) return setErr("Enter the full 6-digit code.");

    setVerifying(true);
    try {
      const otpType = localStorage.getItem("otp_type") || "signup";

      const { error } = await supabase.auth.verifyOtp({
        email,
        token: code,
        type: otpType as "signup" | "email",
      });

      if (error) throw error;

      localStorage.removeItem("signup_email");
      localStorage.removeItem("otp_type");

      // Partner promo flows skip web onboarding — send to app download
      const partner = localStorage.getItem("partner");
      if (partner === "crossfit-wessex") {
        nav("/crossfit-wessex?verified=1");
      } else {
        nav("/about-you");
      }
    } catch (e: any) {
      setErr("Invalid or expired code");
      setCode("");
      inputRefs.current[0]?.focus();
    } finally {
      setVerifying(false);
    }
  };

  const resend = async () => {
    if (!email || cooldown > 0) return;

    try {
      const otpType = (localStorage.getItem("otp_type") as "signup" | "email") || "signup";

      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: { shouldCreateUser: otpType === "signup" },
      });

      if (error) {
        if ((error as any).status === 429) {
          throw new Error("Too many attempts. Please wait.");
        }
        throw error;
      }

      setCooldown(30);
      setResent(true);
      setErr(null);
      setTimeout(() => setResent(false), 3000);
    } catch (e: any) {
      setErr(e.message || "Failed to resend");
    }
  };

  const handleInput = (index: number, value: string) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    
    if (digit) {
      const newCode = code.slice(0, index) + digit + code.slice(index + 1);
      setCode(newCode.slice(0, 6));
      
      if (index < 5) {
        inputRefs.current[index + 1]?.focus();
      }
      
      if (newCode.length === 6) {
        setTimeout(() => verify(), 150);
      }
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      if (code[index]) {
        setCode(code.slice(0, index) + code.slice(index + 1));
      } else if (index > 0) {
        inputRefs.current[index - 1]?.focus();
        setCode(code.slice(0, index - 1) + code.slice(index));
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const paste = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (paste) {
      setCode(paste);
      inputRefs.current[Math.min(paste.length, 5)]?.focus();
      if (paste.length === 6) {
        setTimeout(() => verify(), 150);
      }
    }
  };

  return (
    <div className="min-h-screen bg-[#ffffff] text-[#0f172a]">
      {/* Header */}
      <header className="flex items-center justify-between px-6 py-4 border-b border-[#e2e8f0]">
        <button
          onClick={() => nav("/signup")}
          className="flex items-center gap-1.5 text-gray-500 hover:text-[#0f172a] transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span className="text-sm">Back</span>
        </button>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-[#a78bfa] flex items-center justify-center">
            <svg className="w-5 h-5 text-[#0f172a]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M13 10V3L4 14h7v7l9-11h-7z" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <span className="font-semibold text-[#0f172a]">Input</span>
        </div>
        <div className="w-16"></div>
      </header>

      {/* Main Content */}
      <main className="flex flex-col items-center justify-center px-4 py-8 min-h-[calc(100vh-140px)]">
        <div className="w-full max-w-md">
          
          {/* Title */}
          <div className="text-center mb-8">
            <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-[#f8fafc] border border-[#e2e8f0] flex items-center justify-center">
              <svg className="w-7 h-7 text-[#a78bfa]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-[#0f172a] mb-2">
              Check your email
            </h1>
            <p className="text-gray-500 text-sm">
              We sent a code to{" "}
              <span className="text-[#0f172a]">{maskEmail(email)}</span>
            </p>
          </div>

          {/* OTP Card */}
          <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-2xl p-6">
            
            {/* OTP Input */}
            <div className="flex justify-center gap-3 mb-6" onPaste={handlePaste}>
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <input
                  key={i}
                  ref={(el) => (inputRefs.current[i] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={code[i] ?? ""}
                  onChange={(e) => handleInput(i, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(i, e)}
                  onFocus={(e) => e.target.select()}
                  className={`
                    w-11 h-13 bg-[#f1f5f9] border-2 rounded-xl
                    text-xl font-semibold text-[#0f172a] text-center
                    focus:outline-none transition-all duration-200
                    ${code[i] 
                      ? "border-[#a78bfa] bg-[#a78bfa]/5" 
                      : "border-[#e2e8f0] focus:border-[#a78bfa]/50"
                    }
                  `}
                />
              ))}
            </div>

            {/* Verify Button */}
            <button
              onClick={verify}
              disabled={verifying || code.length !== 6}
              className="w-full h-12 bg-[#a78bfa] hover:bg-[#a78bfa]/90 disabled:bg-[#f1f5f9] disabled:text-gray-600 text-[#0f172a] font-semibold text-base rounded-xl transition-all duration-200 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {verifying ? (
                <>
                  <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Verifying...
                </>
              ) : "Continue"}
            </button>

            {/* Error */}
            {err && (
              <div className="mt-4 p-3 bg-red-500/10 border border-red-500/20 rounded-xl">
                <p className="text-red-400 text-sm text-center">{err}</p>
              </div>
            )}

            {/* Resent confirmation */}
            {resent && (
              <div className="mt-4 p-3 bg-[#a78bfa]/10 border border-[#a78bfa]/20 rounded-xl">
                <p className="text-[#a78bfa] text-sm text-center">Code sent!</p>
              </div>
            )}

            {/* Resend & Change Email */}
            <div className="flex justify-center gap-4 mt-6 text-sm">
              <button
                onClick={resend}
                disabled={cooldown > 0}
                className="text-gray-500 hover:text-gray-600 disabled:text-gray-700 transition-colors"
              >
                {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
              </button>
              <span className="text-[#475569]">•</span>
              <Link to="/signup" className="text-gray-500 hover:text-gray-600 transition-colors">
                Change email
              </Link>
            </div>
          </div>

          {/* Help text */}
          <p className="text-center text-gray-600 text-xs mt-6">
            Can't find it? Check your spam folder
          </p>

          {/* Step Indicator */}
          <div className="mt-10">
            <StepIndicator current={1} total={5} />
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-4 text-center border-t border-[#e2e8f0]">
        <p className="text-gray-600 text-xs">
          © {new Date().getFullYear()} Input
        </p>
      </footer>
    </div>
  );
}
